/**
 * Bookmarks page - displays all user bookmarks grouped by book
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAllBookmarks, GroupedBookmarks } from '../hooks/useAllBookmarks';
import BookmarkCard from '../components/bookmarks/BookmarkCard';
import {
  BookmarkIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  BookOpenIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const Bookmarks: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedBooks, setExpandedBooks] = useState<Set<string>>(new Set());
  const hasInitiallyExpanded = useRef(false);

  const {
    groupedBookmarks,
    loading,
    error,
    fetchAllBookmarks,
    updateBookmark,
    deleteBookmark,
    totalCount
  } = useAllBookmarks();

  // Check if we should filter by a specific book from URL params
  const filterBookId = searchParams.get('book');

  // Filter bookmarks by search query
  const filteredGroups = groupedBookmarks
    .filter(group => !filterBookId || group.book.id === filterBookId)
    .map(group => ({
      ...group,
      bookmarks: group.bookmarks.filter(b =>
        (b.label?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        b.page_number.toString().includes(searchQuery) ||
        group.book.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }))
    .filter(group => group.bookmarks.length > 0);

  // Expand all by default on first load - use ref to prevent re-expansion
  useEffect(() => {
    if (groupedBookmarks.length > 0 && !hasInitiallyExpanded.current) {
      hasInitiallyExpanded.current = true;
      setExpandedBooks(new Set(groupedBookmarks.map(g => String(g.book.id))));
    }
  }, [groupedBookmarks]);

  // Toggle book expansion
  const toggleBookExpansion = (bookId: string | number) => {
    const id = String(bookId);
    setExpandedBooks(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Clear book filter
  const clearFilter = () => {
    navigate('/bookmarks');
  };

  return (
    <Layout>
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookmarkIcon className={`h-8 w-8 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              My Bookmarks
            </h1>
          </div>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
            {totalCount} {totalCount === 1 ? 'bookmark' : 'bookmarks'} across {groupedBookmarks.length} {groupedBookmarks.length === 1 ? 'book' : 'books'}
          </p>
        </div>

        {/* Filter notice */}
        {filterBookId && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${
            isDarkMode
              ? 'bg-cyan-900/20 border-cyan-800/30'
              : 'bg-cyan-50 border-cyan-200'
          }`}>
            <span className={isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}>
              Showing bookmarks for: <strong>{filteredGroups[0]?.book.title || 'selected book'}</strong>
            </span>
            <button
              onClick={clearFilter}
              className="text-sm font-medium text-cyan-500 hover:text-cyan-400"
            >
              Show all bookmarks
            </button>
          </div>
        )}

        {/* Search */}
        <div className="relative w-full sm:w-96 mb-8">
          <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
            isDarkMode ? 'text-slate-400' : 'text-gray-400'
          }`} />
          <input
            type="text"
            placeholder="Search bookmarks, pages, or books..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl transition-all ${
              isDarkMode
                ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-400 focus:border-cyan-500'
                : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-cyan-500'
            } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={`rounded-xl p-4 mb-6 ${
            isDarkMode ? 'bg-red-900/20 border border-red-800/30' : 'bg-red-50 border border-red-200'
          }`}>
            <p className={isDarkMode ? 'text-red-300' : 'text-red-700'}>{error}</p>
            <button
              onClick={fetchAllBookmarks}
              className="mt-2 text-sm font-medium text-cyan-500 hover:text-cyan-400"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && totalCount === 0 && (
          <div className="text-center py-12">
            <BookmarkIcon className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No bookmarks yet
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Start reading and bookmark pages to save them here
            </p>
            <button
              onClick={() => navigate('/library')}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                boxShadow: '0 0 20px rgba(6, 182, 212, 0.25)',
              }}
            >
              <BookOpenIcon className="h-5 w-5" />
              Browse Library
            </button>
          </div>
        )}

        {/* Grouped Bookmarks */}
        {!loading && filteredGroups.length > 0 && (
          <div className="space-y-6">
            {filteredGroups.map(group => (
              <div
                key={group.book.id}
                className={`rounded-2xl overflow-hidden border ${
                  isDarkMode
                    ? 'bg-slate-900/50 border-slate-700/50'
                    : 'bg-white border-gray-200'
                }`}
              >
                {/* Book Header */}
                <button
                  onClick={() => toggleBookExpansion(group.book.id)}
                  className={`w-full flex items-center gap-4 p-4 transition-colors ${
                    isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Book Cover */}
                  {group.book.cover_presigned_url ? (
                    <img
                      src={group.book.cover_presigned_url}
                      alt={group.book.title}
                      className="w-12 h-16 object-cover rounded-lg shadow-md"
                    />
                  ) : (
                    <div className="w-12 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center shadow-md">
                      <BookOpenIcon className="w-6 h-6 text-white opacity-50" />
                    </div>
                  )}

                  <div className="flex-1 text-left">
                    <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {group.book.title}
                    </h2>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {group.book.author} | {group.bookmarks.length} {group.bookmarks.length === 1 ? 'bookmark' : 'bookmarks'}
                    </p>
                  </div>

                  <ChevronDownIcon
                    className={`w-5 h-5 transition-transform ${
                      expandedBooks.has(String(group.book.id)) ? 'rotate-180' : ''
                    } ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                  />
                </button>

                {/* Bookmarks Grid */}
                {expandedBooks.has(String(group.book.id)) && (
                  <div className={`p-4 pt-0 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {group.bookmarks.map(bookmark => (
                        <BookmarkCard
                          key={bookmark.id}
                          bookmark={bookmark}
                          onUpdate={updateBookmark}
                          onDelete={deleteBookmark}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* No results from search */}
        {!loading && searchQuery && filteredGroups.length === 0 && totalCount > 0 && (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No matching bookmarks
            </h3>
            <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
              Try a different search term
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Bookmarks;
